import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  confirmModal,
  cancelModal,
  hideModal,
} from '@store/actions/commonAction';

import SatButton from '@components/common/satUI/SatButton';
import SatDim from './SatDim';

const SatModal = props => {
  const {
    visible,
    setVisible,
    type = 'info',
    id,
    wide = false,
    onCloseButton = false,
    hideCloseButton = false,
    msg = '',
    title = '',
    render = undefined,
    children = undefined,
    onOk = undefined,
    onCancel = undefined,
    isPortal = false,
    isScroll = false,
    moveFocus = false,
    isPdf = false,
    isNotification = false,
    setModalZIndex,
  } = props;

  // 모달이 노출(visible)되어 있을 때,
  // type이 'info' / 'newInfo' / 'newInfo2' / 'confirm' 만 KeyStroke 활성화
  const isKeyStrokeAvaliable =
    visible && ['info', 'newInfo', 'newInfo2', 'confirm'].includes(type);

  const dispatch = useDispatch();

  const { isLockScreen } = useSelector(state => state.commonReducer);
  const [modalId] = useState(id ?? $utils.getRandomId());
  const [zIndex, setZIndex] = useState(250);
  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (visible) initModal();
  }, [visible]);

  useEffect(() => {
    /*
      모달 팝업 z-index 셋팅시 dim 영역이 팝업 앞으로 오는 현상으로 
      팝업이 반짝이는 것처럼 보여서 z-index 다 셋팅 후 팝업 표시하도록 수정
    */
    setIsOpened(visible);
  }, [visible, zIndex]);

  const initModal = () => {
    setZIndex($utils.getModalZIndex());
    if (setModalZIndex) setModalZIndex($utils.getModalZIndex() - 1);
  };

  const closeModal = () => {
    if (setVisible) setVisible();
    if (render && typeof render === 'function') dispatch(hideModal());
  };

  const OnCloseRender = ({ handleCloseModal, isPdf }) =>
    !isPdf ? (
      <span className="btn-layer-close" onClick={handleCloseModal} />
    ) : null;

  const getModalClass = () => {
    let className = 'modal';

    if (isNotification) className += ' pop-input-keypad ';
    if (isScroll) className += ' modal-scroll ';

    switch (type) {
      case 'info':
        return className + ` layer-info ${wide ? 'wide' : ''}`;
      case 'type-a':
        return className + ` layer-info ${wide ? 'wide' : ''} type-a`;
      case 'confirm':
        return className + ` layer-default ${wide ? 'wide' : ''} `;
      case 'keypad':
        return className + ` layer-default keypad`;
      case 'slide':
        return className + ` layer-slide ${visible ? 'open' : ''}`;
      case 'list':
        return className + ` layer-info wide open`;
      case 'fullPop':
        return className + ` popup ${visible ? 'open' : ''}`;
      case 'lottie':
        return className + ` layer-info ${visible ? 'open' : ''} lottie-wrap`;
      case 'newInfo':
        return className + ` manage-layer-info layer-info wide open`;
      case 'newInfo2':
        return className + ` manage-layer-info layer-info wide2 open`;
      case 'swiper':
        return (
          className + ` manage-layer-info swiper-layer layer-info wide open`
        );
      case 'pop-setion':
        return className + ` pop-setion ${visible ? 'open' : ''}`;
      default:
        return className + ` layer-info ${wide ? 'wide' : ''}`;
    }
  };

  const ButtonGroups = props => {
    const { type, onOk, onCancel, confirmModal, cancelModal, moveFocus } =
      props;

    return type === 'info' ? (
      <div className="layer-footer">
        <SatButton
          styleClass="btn-s blue btn-wide"
          label={onOk.label}
          onClick={() => confirmModal(onOk.handle)}
          moveFocus={moveFocus}
        />
      </div>
    ) : (
      <div className="btn-group">
        <SatButton
          styleClass="btn-half cancel"
          label={onCancel.label}
          onClick={() => cancelModal(onCancel.handle)}
        />
        <SatButton
          styleClass="btn-half confirm"
          label={onOk.label}
          onClick={() => confirmModal(onOk.handle)}
        />
      </div>
    );
  };

  const RenderNode = <>{typeof render === 'function' && render()}</>;
  const ChildrenNode = <>{typeof children !== 'undefined' && children}</>;

  const handleKeyStroke = key => {
    if (key === 'Enter') {
      dispatch(confirmModal(onOk?.handle));
    } else if (key === 'Escape') {
      dispatch(cancelModal(onCancel?.handle));
    }
  };

  // 키패드 입력 추가
  $hooks.useKeyStroke({
    target: modalId,
    isDisabled: !isKeyStrokeAvaliable,
    onKeyStroke: handleKeyStroke,
  });

  // useEffect(() => {
  //   document.addEventListener('keydown', handleKeyStroke);
  //   return () => document.removeEventListener('keydown', handleKeyStroke);
  // }, []);

  return visible ? (
    <>
      <ModalRenderer isPortal={isPortal}>
        {!isLockScreen && <SatDim isShow={visible} zIndex={zIndex} />}

        <div
          className={getModalClass()}
          style={{ display: visible ? 'block' : 'none', zIndex: zIndex + 1 }}
        >
          {render || children ? (
            <>
              {RenderNode}
              {ChildrenNode}
            </>
          ) : (
            <>
              {['info', 'confirm'].includes(type) && msg !== '' && (
                <div className="cont">
                  {title !== '' && <h3 className={'layer-tit'}>{title}</h3>}
                  <p className="layer-text text-center">
                    {msg?.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </p>
                </div>
              )}

              <ButtonGroups
                type={type}
                onOk={Object.assign(
                  {
                    label: onOk?.okLabel ? onOk?.okLabel : '확인',
                    handle: () => {},
                  },
                  onOk,
                )}
                onCancel={Object.assign(
                  {
                    label: onCancel?.cancelLabel
                      ? onCancel?.cancelLabel
                      : '취소',
                    handle: () => {},
                  },
                  onCancel,
                )}
                confirmModal={cb => dispatch(confirmModal(cb))}
                cancelModal={cb => dispatch(cancelModal(cb))}
                moveFocus={moveFocus}
              />
            </>
          )}

          {!hideCloseButton &&
            (onCloseButton ||
              type === 'slide' ||
              type === 'keypad' ||
              wide) && (
              <OnCloseRender
                handleCloseModal={() => closeModal()}
                isPdf={isPdf}
              />
            )}
        </div>
      </ModalRenderer>
    </>
  ) : (
    <></>
  );
};

const ModalRenderer = ({ children, isPortal }) => {
  return isPortal ? createPortal(children, document.body) : children;
};

export default React.memo(SatModal);
